import React, { useState, forwardRef, useEffect, useRef, useMemo } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
// @mui
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { Box, Grid, Typography } from '@mui/material';
import { LiveChatWidget } from '@livechat/widget-react';
import ReactPixel from 'react-facebook-pixel';
import FloatingIcons from '../../components/FloatingIcons';

import Iconify from '../../components/Iconify';
// hooks
import useSettings from '../../hooks/useSettings';
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import useAuth from '../../hooks/useAuth';

//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import Footer from './Footer';

import Image from '../../components/Image';
import palette from '../../theme/palette';

import { getGameCategsAndCompanies, getPages, getSiteSettings, getUserMemberBalance } from '../../redux/slices/lookup';

import useLocales from '../../hooks/useLocales';

import { openTransactionsDialog } from '../../redux/slices/transactionsDialog';

import { setGameCategories, setActiveTab } from '../../redux/slices/game';

import DepositWithdrawDialog from './header/DepositWithdrawDialog';

import conObj from '../../utils/connection-assistant';
import { isSafariOrIOS } from '../../utils/isSafariOrIOS';

import { usePageVisibility } from '../../hooks/usePageVisibility';
import WithdrawFormDialog from './header/withdraw/WithdrawFormDialog';
import { openLoginSignup } from '../../redux/slices/authPages';

import HistoryNav from '../../assets/bottom-nav/History.png';
import HomeNav from '../../assets/bottom-nav/Home.png';
import LiveChatNav from '../../assets/bottom-nav/LiveChat.png';
import PromoNav from '../../assets/bottom-nav/Promo.png';
import SettingsNav from '../../assets/bottom-nav/Settings.png';
import FloatingButton from '../../components/FloatingButton';

import TelegramIcon from '../../assets/social/telegram.png';
import WhatsappIcon from '../../assets/social/wa.png';
import usePusher from '../../hooks/usePusher';
// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ showDownloadApp, isDesktop, collapseClick, theme, isRoot, showGradientBg, isios }) => ({
  flexGrow: 1,
  // eslint-disable-next-line no-nested-ternary
  background: showGradientBg ? (isios ? theme.palette.gradients.theme3Ios : theme.palette.gradients.theme3) : '#F6F7F9',
  // paddingTop: HEADER.MOBILE_HEIGHT + 24,
  position: 'relative',
  // paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  height: '100%',
  minHeight: 'fit-content',
  [theme.breakpoints.only('xs')]: {
    paddingTop: isRoot ? HEADER.MOBILE_HEIGHT : 0,
  },
  [theme.breakpoints.up('lg')]: {
    // paddingLeft: 8,
    // paddingRight: 8,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    // paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    // ...(collapseClick && {
    //   marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    // }),
  },
}));

const BottomMenuLabel = styled(Typography)(({ theme }) => ({
  fontWeight: ICON_SIZE,
  textTransform: 'uppercase',
  [theme.breakpoints.only('xs')]: {
    fontSize: '2.4vw',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5vw',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1vw',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '0.7rem',
  },
}));

const CurvedText = styled('text')(({ theme }) => ({
  fontWeight: 600,
  textTransform: 'uppercase',
  fill: '#fff',
  letterSpacing: '3px',
  fontSize: '6px',
}));

const BottomMenuBox = styled(Box)(({ theme }) => ({
  background: '#ff0000ff',
  width: '100%',
  height: NAVBAR.MOBILE_MENU,
  position: 'fixed',
  bottom: 0,
  left: 0,
  zIndex: 100,
  display: 'block',
  right: 0,
  boxShadow: '0px 0px 15px 6px rgba(255, 255, 255, 0.2)',
  borderTopLeftRadius: '30px',
  borderTopRightRadius: '30px',
}));

const MenuGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexDirection: 'column',
  // backgroundColor: '#1F1F1F',
}));

const CurvedLabel = ({ children }) => {
  return (
    <svg width="120" height="30">
      <defs>
        <path id="curveText" d="M 10,46 A 50,50 0 0 1 108,50" />
      </defs>

      <CurvedText
        sx={{
          zIndex: 1,
        }}
      >
        <textPath href="#curveText" startOffset="50%" textAnchor="middle">
          {children}
        </textPath>
      </CurvedText>
    </svg>
  );
};

const ICON_SIZE = 30;

// ----------------------------------------------------------------------

function updatePos(elem, width, widgetState) {
  console.log(`widgetState = ${widgetState}`);
  if (width < 600 && widgetState === 'widgetClose') {
    elem.style.setProperty('bottom', '25vw', 'important');
    elem.style.setProperty('right', '2vw', 'important');
    elem.style.opacity = 1;
  } else if (width < 600 && widgetState === 'widgetOpen') {
    elem.style.setProperty('bottom', '0px', 'important');
    elem.style.setProperty('right', '0px', 'important');
    elem.style.opacity = 1;
  } else if (width < 600) {
    elem.style.setProperty('bottom', '25vw', 'important');
    elem.style.setProperty('right', '2vw', 'important');
    elem.style.opacity = 1;
  } else {
    elem.style.setProperty('bottom', '25px', 'important');
    elem.style.setProperty('right', '25px', 'important');
    elem.style.opacity = 1;
  }
}

const INACTIVE_ICON = {
  home: require('../../assets/side-nav/home.png'),
  promo: require('../../assets/side-nav/promo_outlined.png'),
  cs: require('../../assets/side-nav/cs.png'),
  acc: require('../../assets/side-nav/acc.png'),
};

const ACTIVE_ICON = {
  home: require('../../assets/side-nav/home_active.png'),
  promo: require('../../assets/side-nav/promo_active.png'),
  cs: require('../../assets/side-nav/cs_active.png'),
  acc: require('../../assets/side-nav/acc_active.png'),
};

const DashboardLayout = () => {
  const { isAuthenticated } = useAuth();

  const [siteConfigs, setSiteConfigs] = useState(null);

  const { collapseClick, isCollapse, onToggleCollapse } = useCollapseDrawer();

  const { isOpenTransactionsDialog } = useSelector((x) => x.transactionsDialog);

  const { isAddMemberBankDialog } = useSelector((x) => x.addMemberBankDialog);

  const { siteSettings, contactUs } = useSelector((x) => x.lookup);

  const { activeTab } = useSelector((x) => x.game);

  const isVisible = usePageVisibility();

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  const [showDownloadApp, setShowDownloadApp] = useState(false);

  const verticalLayout = themeLayout === 'vertical';

  const [element, setElement] = useState(null);

  const [width, setWidth] = useState(window.innerWidth);

  const [height, setHeight] = useState(window.innerHeight);

  const [liveChat, setLiveChat] = useState(null);

  const location = useLocation();

  const isRoot = location.pathname === '/';

  const intervalRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const updateDimensions = () => {
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
  };

  const isShowMainBgGradient = useMemo(() => {
    let isShow = true;

    if (location?.pathname?.startsWith('/games')) isShow = false;
    if (location?.pathname?.indexOf('404') !== -1) isShow = false;

    return isShow;
  }, [isDesktop, location?.pathname]);

  useEffect(() => {
    console.log(`isSafariOrIOS = ${isSafariOrIOS}`);
    getPages();
  }, []);

  useEffect(() => {
    if (!isDesktop && location.pathname === '/') {
      dispatch(setActiveTab('slot'));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (contactUs) {
      const _livechatContent = contactUs?.find((f) => f.type === 'LIVECHAT')?.content;
      if (_livechatContent) setLiveChat(_livechatContent);
    }
  }, [contactUs]);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);

    // Check if the PWA is already installed (usually stored in localStorage or can be checked with appinstalled event)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowDownloadApp(false);
    }

    // Listen for the `appinstalled` event
    window.addEventListener('appinstalled', () => {
      setShowDownloadApp(false);
    });

    return () => {
      window.removeEventListener('resize', updateDimensions);

      window.removeEventListener('appinstalled', () => {
        setShowDownloadApp(false);
      });
    };
  }, []);

  useEffect(() => {
    const promises = [getGameCategsAndCompanies(), getSiteSettings(), getAllGames()];

    // if (isAuthenticated) promises.push(getUserMemberBalance());
    Promise.all(promises);
  }, [isAuthenticated]);

  /**
   * Call getUserMemberBalance() when tab is active and isAuthenticated
   * to keep the member balance updated
   */
  useEffect(() => {
    if (isVisible && isAuthenticated) {
      getUserMemberBalance();
      intervalRef.current = setInterval(() => {
        getUserMemberBalance();
      }, 30000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // if (isVisible) {
    //   if (localStorage.getItem(`temp-lang`)) {
    //     onChangeLang(localStorage.getItem(`temp-lang`));
    //     localStorage.setItem(`lang${params?.domain?.toUpperCase()}`, localStorage.getItem(`temp-lang`));
    //     localStorage.removeItem(`temp-lang`);
    //     navigate('/');
    //     window.location.reload();
    //   } else if (!localStorage.getItem(`lang${params?.domain?.toUpperCase()}`)) {
    //     localStorage.setItem(`lang${params?.domain?.toUpperCase()}`, 'en');
    //     onChangeLang('en');
    //   } else {
    //     onChangeLang(localStorage.getItem(`lang${params?.domain?.toUpperCase()}`));
    //   }
    // }
  }, [isVisible, isAuthenticated]);

  useEffect(() => {
    try {
      if (siteSettings?.config_analytics) {
        const _ga = siteSettings?.config_analytics?.split(',');

        // Multiple products (previously known as trackers)
        ReactGA.initialize(
          _ga.map((item) => ({
            // measurement ID
            trackingId: item,
            // gaOptions: {...}, // optional
            // gtagOptions: {...}, // optional
          }))
          //   [
          //   {
          //     // measurement ID
          //     trackingId: _ga[0],
          //     // gaOptions: {...}, // optional
          //     // gtagOptions: {...}, // optional
          //   },
          // ]
        );
      }

      if (siteSettings?.config_fb_pixel) {
        const metaPixel = siteSettings.config_fb_pixel.replace(/[^\d,]+/g, '').split(',');
        const options = {
          autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
          debug: false, // enable logs
        };

        metaPixel.forEach((_pixel) => {
          ReactPixel.init(_pixel, options);

          ReactPixel.pageView(); // For tracking page view
        });
      }

      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const script = document.createElement('script');

      //   script.id = respondIo?.id;
      //   script.src = respondIo?.src;
      //   script.async = true;

      //   document.head.appendChild(script);
      // }
    } catch (error) {
      console.warn(`3RD PARTY INTEGRATION ERROR:  ${error?.message}`);
    }
  }, [siteSettings]);

  useEffect(() => {
    const selector = '#_68625 > div > iframe';

    let observer;

    function handleMutation(mutations) {
      if (document.querySelector(selector)) {
        observer?.disconnect();
        setElement(document.querySelector(selector));
      }
    }

    if (document.querySelector(selector)) {
      setElement(document.querySelector(selector));
    } else {
      const observer = new MutationObserver(handleMutation);
      observer?.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer?.disconnect(); // Cleanup when the component unmounts
      };
    }
  }, []); // The empty dependency array ensures this effect runs only once

  useEffect(() => {
    if (element) {
      console.log('iframe Element is ready');
      element.style.opacity = 0;

      // it seems their js sets the bottom position with a slight delay after load, so we wait 1 sec then only set our css
      setTimeout(() => {
        const _widgetState = element?.getAttribute('state');
        updatePos(element, width, _widgetState);

        // observe attribute change
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
              const widgetState = element?.getAttribute('state');

              console.log(`widgetState = ${widgetState}`);

              console.log('attributes changed');
              // set the css again
              updatePos(element, width, widgetState);
            }
          });
        });

        observer.observe(element, {
          attributes: true, // configure it to listen to attribute changes
        });
      }, 2000);
    }
  }, [element]);

  const memberInfo = useSelector((state) => state.lookup.memberInfo);

  usePusher({
    uuid: memberInfo?.uuid,
    navigate,
    setLoading,
  });

  const getAllGames = () => {
    // conObj.post('games/all.json?limit=1').then((res) => {
    //   if (res.status === 200) {
    //     // res.data?.data?.data
    //     // set appversion to localstorage
    //     // res.data?.data.appversion
    //     const _allGames = res.data?.data?.data;
    //     dispatch(
    //       setGameCategories(
    //         _allGames
    //           .filter((f) => f.key.toLowerCase().indexOf('gcat') !== -1)
    //           .map((item) => ({ key: item.key, name: item.name, slug: item.slug }))
    //       )
    //     );
    //   }
    // });
  };

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader
          onOpenSidebar={() => setOpen(true)}
          verticalLayout={verticalLayout}
          showDownloadApp={showDownloadApp}
          setShowDownloadApp={setShowDownloadApp}
        />

        {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
        )}

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
        height: '100%',
        // backgroundColor: '#0b1712',
      }}
    >
      <NavbarVertical handleSideBar={() => setOpen(!open)} isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
      <FloatingIcons />
      {siteSettings?.config_telegram && !siteSettings?.config_livechat ? <FloatingButton /> : <></>}
      <MainStyle
        collapseClick={collapseClick}
        showDownloadApp={showDownloadApp}
        isDesktop={isDesktop}
        isRoot={isRoot}
        showGradientBg={isShowMainBgGradient}
        isios={isSafariOrIOS}
      >
        {/* {(!isDesktop || (isDesktop && location?.pathname?.indexOf('games') !== -1)) && (
          <img
            alt="Background"
            src={require('../../assets/background.png')}
            style={{ position: 'absolute', top: `${isDesktop ? 'unset' : '-8px'}`, width: 'stretch' }}
          />
        )} */}

        {(isDesktop || isRoot) && (
          <DashboardHeader
            isCollapse={isCollapse}
            isOpenSidebar={open}
            onOpenSidebar={() => setOpen(true)}
            onCloseSidebar={() => setOpen(!open)}
            onToggleCollapse={onToggleCollapse}
            showDownloadApp={showDownloadApp}
            setShowDownloadApp={setShowDownloadApp}
          />
        )}

        <Outlet context={[showDownloadApp]} />
        {/* <Footer /> */}
        {!isDesktop && (
          <>
            <Box sx={{ height: '64px', width: '100%' }} />
            <Box
              sx={{
                height: '64px',
                background: '#fff',
                backgroundSize: 'cover',
                position: 'fixed',
                bottom: 0,
                left: 0,
                zIndex: 100,
                boxShadow: '35.5px -2px 0px #B2B7C71F',
                display: 'block',
                p: '5px 0',
                right: 0,
                width: isDesktop && !isCollapse ? `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)` : '100%',
                marginLeft: isDesktop && !isCollapse ? `calc(${NAVBAR.DASHBOARD_WIDTH}px)` : '0',
              }}
            >
              <Grid container sx={{ height: '100%' }}>
                <MenuGrid
                  onClick={() => {
                    // dispatch(setActiveTab('slot'));
                    navigate('/');
                  }}
                  item
                  xs={3}
                >
                  <img
                    alt="Menu"
                    src={activeTab === 'slot' ? ACTIVE_ICON.home : INACTIVE_ICON.home}
                    style={{
                      width: ICON_SIZE,
                      height: ICON_SIZE,
                      display: 'block',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    color={activeTab === 'slot' ? 'primary.main' : 'primary.lightText'}
                    sx={{ fontSize: '12px', fontWeight: 500 }}
                  >
                    {translate('home')}
                  </Typography>
                </MenuGrid>
                <MenuGrid
                  onClick={() => {
                    dispatch(setActiveTab('promotion'));
                    navigate('/promotion');
                  }}
                  item
                  xs={3}
                >
                  <img
                    alt="Menu"
                    src={activeTab === 'promotion' ? ACTIVE_ICON.promo : INACTIVE_ICON.promo}
                    style={{
                      width: ICON_SIZE,
                      height: ICON_SIZE,
                      display: 'block',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    color={activeTab === 'promotion' ? 'primary.main' : 'primary.lightText'}
                    sx={{ fontSize: '12px', fontWeight: 500 }}
                  >
                    {translate('promo')}
                  </Typography>
                </MenuGrid>
                <MenuGrid
                  onClick={() => {
                    dispatch(setActiveTab('cs'));
                    navigate('/cs');
                  }}
                  item
                  xs={3}
                >
                  <img
                    alt="Menu"
                    src={activeTab === 'cs' ? ACTIVE_ICON.cs : INACTIVE_ICON.cs}
                    style={{
                      width: ICON_SIZE,
                      height: ICON_SIZE,
                      display: 'block',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    color={activeTab === 'cs' ? 'primary.main' : 'primary.lightText'}
                    sx={{ fontSize: '12px', fontWeight: 500 }}
                  >
                    {translate('CS')}
                  </Typography>
                </MenuGrid>

                <MenuGrid
                  onClick={() => {
                    if (!isAuthenticated) navigate('/auth/login');
                    else {
                      dispatch(setActiveTab('acc'));
                      navigate('/user/account');
                    }
                  }}
                  item
                  xs={2.4}
                >
                  <img
                    alt="Menu"
                    src={activeTab === 'acc' ? ACTIVE_ICON.acc : INACTIVE_ICON.acc}
                    style={{
                      width: ICON_SIZE,
                      height: ICON_SIZE,
                      display: 'block',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    color={activeTab === 'acc' ? 'primary.main' : 'primary.lightText'}
                    sx={{ fontSize: '12px', fontWeight: 500 }}
                  >
                    {translate('acc')}
                  </Typography>
                </MenuGrid>
              </Grid>
            </Box>
          </>
        )}
      </MainStyle>
      {liveChat && (
        <LiveChatWidget
          license={liveChat}
          onReady={() => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer) {
              widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
          onVisibilityChanged={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer) {
              if (data.visibility === 'maximized') {
                widgetContainer.classList.remove('adjust-bottom-space');
              } else widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
        />
      )}
      {isOpenTransactionsDialog.open && <DepositWithdrawDialog />}
      {isAddMemberBankDialog?.open && <WithdrawFormDialog />}
    </Box>
  );
};

export default DashboardLayout;
